"use strict";
/**
 * Template-binding encoding — single source of truth for the
 * `{{steps.<stepId>.<fieldId>}}` and `{{credential.<id>.<field>}}`
 * template forms.
 *
 * Writer (editor), reader (workflow-health), template-parser
 * (code-editor references), and runtime resolver all go through
 * this module so the encoding cannot drift between consumers.
 *
 * Grammar:
 *   step-ref       := "{{" ws? "steps."      stepId "." fieldPath ws? "}}"
 *   credential-ref := "{{" ws? "credential." credId "." fieldPath ws? "}}"
 *   id / segment   := [A-Za-z0-9_-]+
 *   fieldPath      := segment ("." segment)*
 *
 * Matches the runner-side template-resolver contract: steps resolve by
 * navigating the output object after the step id. The legacy `.output.`
 * prefix remains rejected by the parser to avoid reintroducing the
 * pre-sl-fpi2.3 transport shape.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.STRICT_CONFIG_BINDING_RE = exports.CONFIG_REF_RE = exports.TEMPLATE_REF_RE = exports.PLATFORM_CREDENTIAL_TYPES = exports.CREDENTIAL_BINDING_RE = exports.STEP_BINDING_RE = void 0;
exports.isPlatformCredentialType = isPlatformCredentialType;
exports.encodeStepFieldBinding = encodeStepFieldBinding;
exports.encodeCredentialBinding = encodeCredentialBinding;
exports.parseStepFieldBinding = parseStepFieldBinding;
exports.parseCredentialBinding = parseCredentialBinding;
/** Character class shared by step-id, field-id, credential-id, field. */
const ID_CHARS = '[A-Za-z0-9_-]+';
const FIELD_PATH_CHARS = `${ID_CHARS}(?:\\.${ID_CHARS})*`;
/** Strict anchored match: the entire string is a single step binding. */
exports.STEP_BINDING_RE = new RegExp(`^\\{\\{\\s*steps\\.(${ID_CHARS})\\.(${FIELD_PATH_CHARS})\\s*\\}\\}$`);
/** Strict anchored match: the entire string is a single credential binding. */
exports.CREDENTIAL_BINDING_RE = new RegExp(`^\\{\\{\\s*credential\\.(${ID_CHARS})\\.(${FIELD_PATH_CHARS})\\s*\\}\\}$`);
/**
 * Credentials the platform provides itself. A workspace neither connects
 * nor replaces them, so a reference to one is not a business credential.
 */
exports.PLATFORM_CREDENTIAL_TYPES = [
    'streamlainAiGateway',
    'streamlainApi',
];
function isPlatformCredentialType(id) {
    return exports.PLATFORM_CREDENTIAL_TYPES.includes(id);
}
/**
 * Global scan: every `{{steps.X.Y}}` or `{{credential.X.Y}}` reference
 * embedded inside arbitrary text (code bodies, prompt templates, etc.).
 * Capture groups: (1) kind, (2) id, (3) field.
 */
exports.TEMPLATE_REF_RE = new RegExp(`\\{\\{\\s*(steps|credential)\\.(${ID_CHARS})\\.(${FIELD_PATH_CHARS})\\s*\\}\\}`, 'g');
/**
 * Config-variable grammar — `{{config.key}}` or `{{config.key|default}}`.
 * The default fragment is a single line of arbitrary text (no `}` allowed)
 * and is optional. `key` follows the standard identifier char class.
 *
 * Capture groups: (1) key, (2) default value (may be undefined).
 */
exports.CONFIG_REF_RE = new RegExp(`\\{\\{\\s*config\\.(${ID_CHARS})(?:\\|([^}]*))?\\s*\\}\\}`, 'g');
/**
 * Strict anchored match: the entire string is a single wellformed config
 * binding. Callers scanning for embedded refs inside text should use
 * `CONFIG_REF_RE`; callers validating a candidate token in isolation
 * (publish-time wellformedness gate) use this one.
 */
exports.STRICT_CONFIG_BINDING_RE = new RegExp(`^\\{\\{\\s*config\\.(${ID_CHARS})(?:\\|([^}]*))?\\s*\\}\\}$`);
/** Encode a step+field reference as its template string. */
function encodeStepFieldBinding(stepId, fieldId) {
    return `{{steps.${stepId}.${fieldId}}}`;
}
/** Encode a credential+field reference as its template string. */
function encodeCredentialBinding(credentialId, field) {
    return `{{credential.${credentialId}.${field}}}`;
}
/**
 * Parse a whole-string step binding. Returns null when the input is
 * not a standalone `{{steps.X.Y}}` expression (callers that scan for
 * embedded references inside text should use `TEMPLATE_REF_RE`).
 */
function parseStepFieldBinding(s) {
    if (typeof s !== 'string')
        return null;
    const m = s.match(exports.STEP_BINDING_RE);
    if (!m)
        return null;
    const fieldId = m[2];
    if (fieldId.startsWith('output.'))
        return null;
    return { stepId: m[1], fieldId };
}
/** Parse a whole-string credential binding. */
function parseCredentialBinding(s) {
    if (typeof s !== 'string')
        return null;
    const m = s.match(exports.CREDENTIAL_BINDING_RE);
    if (!m)
        return null;
    return { credentialId: m[1], field: m[2] };
}
